import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiClient, getData, saveData } from '../configurations/index.config';

// Define types for user data and profile
export interface ProfileData {
  userId: number;
  username: string;
  employeeId?: number | null;
  status: 'active' | 'inactive';
  employee?: any | null;
  roleId?: number | null;
  privileges: string[];
  [key: string]: unknown;
}

export interface AppContextValue {
  userData: string | null;
  profileData: ProfileData | null;
  saveUser: (token: string, profile: ProfileData) => void;
  clearUser: () => void;
  isAuthenticated: boolean;
}

const AppContext = createContext<AppContextValue>({
  userData: null,
  profileData: null,
  saveUser: () => {},
  clearUser: () => {},
  isAuthenticated: false,
});

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [userData, setUserData] = useState<string | null>(null);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  // Loading state managed by individual components
  const navigate = useNavigate();

  useEffect(() => {
    const savedUser = getData('token') as string | null;
    const savedProfile = getData('profileData') as ProfileData | null;

    if (savedUser) {
      setUserData(savedUser);
      ApiClient.defaults.headers.common['Authorization'] = `Bearer ${savedUser}`;
    }
    if (savedProfile) {
      setProfileData(savedProfile);
    }

    // Loading complete

    // Only redirect if not loading and no profile exists
    if (!savedProfile && window.location.pathname.startsWith('/account')) {
      navigate('/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount - navigate is stable

  const saveUser = (token: string, profile: ProfileData): void => {
    setUserData(token);
    setProfileData(profile);
    saveData('token', token);
    saveData('profileData', profile);
    ApiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const clearUser = (): void => {
    setUserData(null);
    setProfileData(null);
    localStorage.removeItem('token');
    localStorage.removeItem('profileData');
    delete ApiClient.defaults.headers.common['Authorization'];
    navigate('/', { replace: true });
  };

  const isAuthenticated = Boolean(userData && profileData);

  return (
    <AppContext.Provider value={{ userData, profileData, saveUser, clearUser, isAuthenticated }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = (): AppContextValue => useContext(AppContext);
